import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import TableCommon from "../Table";
import useGetFilesDownloadSequencePackage from "src/components/Sequence/useGetFilesDownloadSequencePackage";
import { PAGINATION_DEFAULT } from "src/constant/common";
import isEmpty from "lodash/isEmpty";
import useDownloader from "react-use-downloader";
import JSZip from "jszip";
import TooltipWrapper from "../TooltipWrapper";

const columns = ({ t }) => [
  {
    dataField: "file_name",
    text: t("download_modal.file_name"),
    headerClass: "text-nowrap",
    formatter: (_, row) => {
      return (
        <TooltipWrapper
          id={`tooltip-filename-${row?.id}`}
          tooltipText={row?.file_name || ""}
          checkOverflow={true}
          tooltipClassName="max-width-by-px-300"
        >
          <p className="mb-0 max-width-by-px-224 min-width-by-px-80 overflow-hidden text-truncate">{row?.file_name || ""}</p>
        </TooltipWrapper>
      );
    },
  },
];

const ModalDownloadSequence = ({ sequencesData = [], isOpen, toggle }) => {
  const { t } = useTranslation("sequence", "common");
  const [filesSelected, setFilesSelected] = useState([]);
  const [filesDownloadSequence, setFilesDownloadSequence] = useState([]);
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const { download, isInProgress } = useDownloader();

  const { data: filesDownloadData, isFetching: isLoadingFiles } = useGetFilesDownloadSequencePackage({
    limit: PAGINATION_DEFAULT.pageSize,
    data: sequencesData,
  });

  const handleCancel = () => {
    toggle();
    document.body.click();
  };

  const getSelectedSequences = (sequences) => {
    if (sequences.length === 0) return "";

    const selectedSequences = sequences.map((sequence) => sequence.combination_id).join(", ");

    return selectedSequences;
  };

  const fetchFile = async (file) => {
    try {
      const response = await fetch(file.download_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${file.file_name}: ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      console.error(`Error fetching file ${file.file_name}:`, error);
      throw error;
    }
  };

  const addFilesToZip = async (zip, files) => {
    const filePromises = files.map(async (file) => {
      try {
        const blob = await fetchFile(file);
        zip.file(file.file_name, blob);
        return { success: true, fileName: file.file_name };
      } catch (error) {
        return { success: false, fileName: file.file_name, error };
      }
    });

    return await Promise.all(filePromises);
  };

  const generateZipFileName = () => {
    const date = new Date().toISOString().split("T")[0];
    return `sequence_files_${date}.zip`;
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fallbackToIndividualDownloads = (files) => {
    files.forEach((file) => {
      download(file.download_url, file.file_name);
    });
  };

  const createAndDownloadZip = async (files) => {
    const zip = new JSZip();
    const results = await addFilesToZip(zip, files);

    const failedFiles = results.filter((result) => !result.success);
    if (failedFiles.length > 0) {
      console.warn(`Failed to add ${failedFiles.length} files to zip:`, failedFiles);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const fileName = generateZipFileName();
    downloadBlob(zipBlob, fileName);

    return results;
  };

  const handleDownloadFiles = async () => {
    if (isEmpty(filesSelected)) return;

    setIsCreatingZip(true);

    try {
      if (filesSelected.length === 1) {
        const file = filesSelected[0];
        download(file.download_url, file.file_name);
      } else {
        await createAndDownloadZip(filesSelected);
      }
    } catch (error) {
      console.error("Error downloading files:", error);
      fallbackToIndividualDownloads(filesSelected);
    } finally {
      setIsCreatingZip(false);
    }
  };

  useEffect(() => {
    if (filesDownloadData) {
      const data = filesDownloadData?.items || [];
      const dataModified = data
        .map((item) => {
          const combinedId = `#${item.yamcs_package_increment_id}-${item.test_package_input_id}`;
          return item?.files.map((file, index) => ({
            ...file,
            file_name: `${combinedId}-${file.file_name}`,
            id: `${combinedId}_${item.sequence_package_input_id}_${index}`,
          }));
        })
        .flat();

      setFilesDownloadSequence(dataModified);
    }
  }, [filesDownloadData]);

  return (
    <Modal centered isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" className="tag-manage-modal">
      <ModalHeader>{t("download_modal.title")}</ModalHeader>
      <ModalBody className="p-b-0 overflow-auto">
        {isLoadingFiles ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <Spinner size="sm" />
            <span className="ms-2">{t("download_modal.loading_files")}</span>
          </div>
        ) : (
          <>
            <div className="p-x-16 mb-2">
              <strong className="text-break">{`${t("download_modal.select_files_in_sequence")}: ${getSelectedSequences(
                sequencesData
              )}`}</strong>
            </div>
            <TableCommon
              loading={isLoadingFiles}
              columns={columns({ t })}
              data={filesDownloadSequence}
              tableFixed
              tableClassName="table striped table-collapse table-fixed sequences-table table-hover"
              customClassName="max-height-by-px-416 overflow-y-auto"
              classTableHead="sticky-top bg-white"
              hasPagination={false}
              enableRowSelection
              onRowSelectionChange={(sequenceData) => setFilesSelected(sequenceData)}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-center p-a-16">
        <div className="d-flex justify-content-center p-a-16">
          <button
            type="button"
            className="btn btn-sm btn-secondary m-x-10 width-by-px-160"
            disabled={isInProgress || isCreatingZip}
            onClick={handleCancel}
          >
            {t("cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-sm btn-primary m-x-10 width-by-px-160 text-nowrap"
            disabled={isEmpty(filesDownloadSequence) || isEmpty(filesSelected) || isInProgress || isCreatingZip}
            onClick={handleDownloadFiles}
          >
            {isInProgress || isCreatingZip ? <Spinner size="sm" /> : t("download_modal.download_sequences_selected")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ModalDownloadSequence;
